import React, { useCallback, useEffect, useState } from 'react';

import {
  Chip,
  EmptyState,
  ErrorState,
  ListaSelecao,
  Modal,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import { fetchSgaCooperativas, Cooperativa } from '../../../api/sga-cooperativa/sga-cooperativaApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha de uma cooperativa para vincular ao usuário (reescrito na at-68 sobre o
 * `Modal` do kit). Par do `ModalRegionais` — as mesmas seis correções estão descritas lá.
 *
 * ⚠️ **Não confundir com `ModalCooperativasUsuario`**: aquele lista as cooperativas **do
 * consultor logado** (escolha dentro do fluxo de adesão/cotação); este lista o catálogo
 * do SGA para o TI vincular ao usuário que está editando.
 *
 * 📌 `ModalCooperativas.css` era um arquivo **vazio** e foi apagado nesta entrega: o
 * estilo vinha, na verdade, do CSS do modal de regionais.
 */

interface ModalCooperativasProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCooperativa: (cooperativa: Cooperativa) => void;
  /** Códigos já vinculados: marcados na lista e não re-selecionáveis. */
  selectedCodigos?: string[];
}

const POR_PAGINA = 10;

type Estado = 'carregando' | 'ok' | 'erro';

const ModalCooperativas: React.FC<ModalCooperativasProps> = ({
  isOpen,
  onClose,
  onSelectCooperativa,
  selectedCodigos = [],
}) => {
  const [cooperativas, setCooperativas] = useState<Cooperativa[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (isOpen) {
      setBusca('');
      setPagina(1);
    }
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchSgaCooperativas(pagina, POR_PAGINA, busca);

      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de cooperativas.');
        setEstado('erro');
        return;
      }

      setCooperativas(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as cooperativas.'));
      setEstado('erro');
    }
  }, [pagina, busca]);

  useEffect(() => {
    if (!isOpen) return;
    void buscar();
  }, [isOpen, buscar]);

  const jaVinculadas = new Set(selectedCodigos);

  const escolher = (cooperativa: Cooperativa) => {
    if (jaVinculadas.has(cooperativa.codigo_cooperativa)) return;
    onSelectCooperativa(cooperativa);
  };

  const vazio = busca ? (
    <EmptyState
      icone="busca"
      titulo="Nenhuma cooperativa encontrada para a busca"
      descricao={`Nada corresponde a "${busca}". Tente escrever menos letras ou buscar pelo código.`}
    />
  ) : (
    <EmptyState
      icone="sem-dados"
      titulo="Nenhuma cooperativa no espelho do SGA"
      descricao="As cooperativas vêm do SGA pela sincronização diária. Se a lista está vazia, rode a sincronização em Administração."
    />
  );

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Cooperativa"
      descricao="A cooperativa define quais cotas e produtos o usuário enxerga."
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && cooperativas.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <SearchInput
          aoBuscar={(termo) => {
            setBusca(termo);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou código"
          rotuloAcessivel="Buscar cooperativa"
        />

        {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-12" />}

        {estado === 'erro' && (
          <ErrorState
            mensagem="Não foi possível carregar as cooperativas."
            detalhe={erro}
            aoTentarNovamente={() => void buscar()}
          />
        )}

        {estado === 'ok' && (
          <ListaSelecao
            itens={cooperativas}
            chave={(c) => c.codigo_cooperativa}
            rotulo={(c) => c.nome || 'Nome não disponível'}
            detalhe={(c) =>
              [
                `Código ${c.codigo_cooperativa}`,
                c.cidade && c.estado ? `${c.cidade}/${c.estado}` : null,
              ]
                .filter(Boolean)
                .join(' · ')
            }
            sufixo={(c) =>
              jaVinculadas.has(c.codigo_cooperativa) ? (
                <Chip variante="neutro">Já vinculada</Chip>
              ) : null
            }
            escolhido={(c) => jaVinculadas.has(c.codigo_cooperativa)}
            aoEscolher={escolher}
            vazio={vazio}
            rotuloAcessivel="Cooperativas disponíveis"
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalCooperativas;
